'use client';

import type { ButtonHTMLAttributes, JSX } from 'react';
import type React from 'react';
import { forwardRef, useState } from 'react';

import clsx from 'clsx';

import type { CorePopoverProps } from 'DesignSystem2/Popover';
import { Popover } from 'DesignSystem2/Popover';
import Padding from 'DesignSystem2/Theme/Padding';
import Body from 'DesignSystem3/Text/Body';
import { noop } from 'utils/noop';

import { Icon } from '../Icon';
import type { IconName } from '../Icon';

const sizeClasses = {
    medium: 'p-1.5 min-w-6 min-h-6',
    small: 'p-1 min-w-5 min-h-5',
    tiny: 'p-0.5 min-w-4 min-h-4',
    minuscule: 'p-0.25',
};

export interface IconButtonCoreProps {
    icon: IconName | JSX.Element;
    label?: string;
    disabled?: boolean;
    variant?: 'default' | 'destructive' | 'brand' | 'success' | 'leaderboard';
    onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
    popoverProps?: CorePopoverProps;
    buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
    color?: string;
}
export interface IconButtonProps extends IconButtonCoreProps {
    rank?: 'primary' | 'secondary' | 'tertiary';
    size?: 'medium' | 'small' | 'tiny' | 'minuscule';
    iconSize?: string;
    style?: React.CSSProperties;
    rotateOnClick?: boolean;
    mirror?: boolean;
}

const getRankStyles = (rank: IconButtonProps['rank'], variant: IconButtonProps['variant']) => {
    const base = 'inline-flex items-center justify-center align-middle rounded-x30 m-0';

    switch (rank) {
        case 'secondary': {
            const secondaryBase = clsx(base, 'transition-colors duration-83');
            switch (variant) {
                case 'destructive':
                    return clsx(
                        secondaryBase,
                        'border border-red-x40 bg-custom-transparent text-red-x10 hover:bg-fill-x50',
                    );
                case 'brand':
                    return clsx(
                        secondaryBase,
                        'border border-[var(--kalshi-palette-brand-x40,rgba(128,236,200,1))] bg-custom-transparent text-brand-primary hover:bg-fill-x50',
                    );
                case 'success':
                    return clsx(
                        secondaryBase,
                        'border border-green-x10 bg-custom-transparent text-green-x10 hover:bg-fill-x50',
                    );
                case 'leaderboard':
                    return clsx(secondaryBase, 'bg-fill-x50 text-fill-x30');
                default:
                    return clsx(
                        secondaryBase,
                        'border border-fill-x40 bg-custom-transparent text-text-x10 hover:bg-fill-x50',
                    );
            }
        }
        case 'tertiary': {
            const tertiaryBase = clsx(base, 'border-0 bg-custom-transparent transition-colors duration-83');
            switch (variant) {
                case 'destructive':
                    return clsx(tertiaryBase, 'text-red-x10 hover:bg-fill-x50');
                case 'brand':
                    return clsx(tertiaryBase, 'text-brand-primary hover:bg-fill-x50');
                case 'success':
                    return clsx(tertiaryBase, 'text-green-x10 hover:bg-fill-x50');
                case 'leaderboard':
                    return clsx(tertiaryBase, 'text-fill-x20');
                default:
                    return clsx(tertiaryBase, 'text-text-x10 hover:bg-fill-x50');
            }
        }
        case 'primary':
        default: {
            const primaryBase = clsx(base, 'border-0 transition-opacity duration-83');
            switch (variant) {
                case 'destructive':
                    return clsx(primaryBase, 'bg-red-x10 text-text-inverse hover:opacity-80');
                case 'brand':
                    return clsx(primaryBase, 'bg-brand-primary text-text-inverse hover:opacity-80');
                case 'success':
                    return clsx(primaryBase, 'bg-green-x20 text-green-x10 hover:opacity-80');
                case 'leaderboard':
                    return clsx(primaryBase, 'bg-fill-x50 text-text-x20 hover:opacity-80');
                default:
                    return clsx(primaryBase, 'bg-fill-x10 text-text-inverse hover:opacity-80');
            }
        }
    }
};

const IconButton = forwardRef(
    (
        {
            icon,
            label,
            rank = 'primary',
            size = 'medium',
            iconSize = '24px',
            disabled = false,
            variant = 'default',
            onClick = noop,
            color = undefined,
            popoverProps,
            buttonProps,
            style,
            rotateOnClick,
            mirror,
            ...otherProps
        }: IconButtonProps,
        ref: React.Ref<HTMLButtonElement>,
    ): JSX.Element => {
        const [isRotating, setIsRotating] = useState(false);

        let sizedIconElement = null;
        if (typeof icon === 'string') {
            const IconElement = Icon[icon];
            sizedIconElement = (
                <IconElement
                    size={iconSize}
                    color={color}
                    className={clsx(rotateOnClick && isRotating && 'animate-rotate', mirror && '-scale-x-100')}
                />
            );
        } else {
            sizedIconElement = icon;
        }

        const handleClick = (event: React.MouseEvent<Element, MouseEvent>) => {
            if (rotateOnClick) {
                setIsRotating(true);
            }
            onClick(event);
        };

        const rankClasses = getRankStyles(rank, variant);

        // `buttonProps` spreads last so callers can override attributes, but a
        // caller-supplied className must merge with, not replace, the rank/size classes.
        const { className: buttonClassName, ...restButtonProps } = buttonProps ?? {};
        const iconButton = (
            <button
                {...otherProps}
                onClick={handleClick}
                disabled={disabled}
                ref={ref}
                style={style}
                onAnimationEnd={() => setIsRotating(false)}
                {...restButtonProps}
                className={clsx(
                    !disabled && 'cursor-pointer transition-all duration-167 active:scale-90 active:duration-50',
                    rankClasses,
                    sizeClasses[size],
                    disabled ? 'opacity-20' : '',
                    buttonClassName,
                )}
            >
                {!!label && (
                    <>
                        <Body.x20>{label}</Body.x20>
                        <Padding spacing={0.5} />
                    </>
                )}
                {sizedIconElement}
            </button>
        );

        if (!!popoverProps) {
            return <Popover {...popoverProps}>{iconButton}</Popover>;
        }

        return iconButton;
    },
);

IconButton.displayName = 'IconButton';
export default IconButton;
